import { SuggestionCallout } from 'components/ui';
import { format, isValid, parse } from 'date-fns';
import parseHtml from 'html-react-parser';
import { useMemo } from 'react';
import { useAppSelector } from 'store';
import { useGetStoreInfoQuery } from 'store/services/segmentsApi';
import { useQuery } from 'utils/useQuery';

type Props = {
  tableData: Record<string, any>[];
  lapseMetricColumns: {
    name: string | undefined;
    label: string | undefined;
    format: string;
    data: string[];
    maxValue: number;
    minValue: number;
  };
  columns: {
    name: string;
    label: string;
    format: string;
  }[];
};

export const ProductCohortSummary = ({
  data,
  cohort_type,
  lapseRangeType,
}: {
  data: Props;
  lapseRangeType: 'calendar' | 'relative';
  cohort_type: 'revenue_growth' | 'repeat_purchase' | 'churn_rate';
}) => {
  const query = useQuery();
  const store_name = query.get('store_name');

  const queryParams = `store_name=${store_name}`;

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !store_name,
  });

  const currency = storeInfo?.currency ?? 'usd';
  const locale = storeInfo?.primary_locale ?? 'en';

  const renderSummary = useMemo(() => {
    const baseStrings = {
      relative: {
        revenue_growth:
          '{{customers}} customers bought {{name}} in their first initial purchase in your store and have since made {{orders}} orders in the specified date range with an average order value of {{aov}}. The total revenue from this {{name}} customer cohort is {{revenue}}. In their first month since signing up, this customer cohort averaged {{arpu_30}} of revenue per customer.',
        churn_rate:
          '{{customers}} customers bought {{name}} in their first initial purchase in your store. Since then {{churned_customers}} ({{churned_percent}}) have churned after being active for an average of days. In their first month since signing up, this customer cohort had {{churnr_30}} customers churn. By the third month, we see {{churnr_90}} churn.',
        repeat_purchase:
          '{{customers}} customers bought {{name}} in their first initial purchase in your store and have had an average {{ano}} orders in the specified date range since signing up. Specifically, {{repeats}} ({{repeat_perc}}) have returned to become repeat purchasers. In their first month since signing up, this customer cohort had {{ror_30}} customers return to make a repeat purchase; by the second month, the percentage of repeat customers increased to {{ror_60}}.',
      },
      calendar: {
        revenue_growth:
          '{{customers}} customers bought {{name}} in their first initial purchase in your store and have since made {{orders}} orders in the specified date range with an average order value of {{aov}}. The total revenue from this {{name}} cohort is {{revenue}}. For those who purchased such product in their first purchase in {{firstLapseMetricName}}, this customer cohort averaged {{firstLapseMetricValue}} of revenue per customer.',
        churn_rate:
          '{{customers}} customers bought {{name}} in their first initial purchase in your store. Since then {{churned_customers}} ({{churned_percent}}) have churned after being active for an average of days. For those who purchased such product in their first purchase in {{firstLapseMetricName}}, this cohort averaged {{firstLapseMetricValue}} of revenue per customer.',
        repeat_purchase:
          '{{customers}} customers bought {{name}} in their first initial purchase in your store and have had an average {{ano}} orders in the specified date range since signing up. Specifically, {{repeats}} ({{repeat_perc}}) have returned to become repeat purchasers. For those who purchased such product in their first purchase in  {{firstLapseMetricName}}, this customer cohort averaged {{firstLapseMetricValue}} of revenue per customer.',
      },
    };
    switch (cohort_type) {
      case 'revenue_growth':
        return (
          <RevenueGrowthSummary
            data={data}
            currency={currency}
            locale={locale}
            base_string={baseStrings[lapseRangeType][cohort_type]}
          />
        );
      case 'repeat_purchase':
        return (
          <RepeatPurchase
            data={data}
            currency={currency}
            locale={locale}
            base_string={baseStrings[lapseRangeType][cohort_type]}
          />
        );
      case 'churn_rate':
        return (
          <ChurnRate
            data={data}
            currency={currency}
            locale={locale}
            base_string={baseStrings[lapseRangeType][cohort_type]}
          />
        );
      default:
        return null;
    }
  }, [cohort_type, currency, data, lapseRangeType, locale]);

  return (
    <div className="mt-4">
      <SuggestionCallout>
        <div className="px-6 py-4">
          <p className="text-black text-opacity-60">What this says</p>
          {renderSummary}
        </div>
      </SuggestionCallout>
    </div>
  );
};

function RepeatPurchase({
  data,
  currency,
  locale,
  base_string,
}: {
  data: Props;
  currency: string;
  locale: string;
  base_string: string;
}) {
  const firstCohort = useMemo(() => {
    return data.tableData.length > 0 ? data.tableData[0] : {};
  }, [data.tableData]);

  const currentProductMeta = useAppSelector((state) => {
    return state.productMeta.find(
      (productMeta) => productMeta.id === firstCohort.cohort_name
    );
  });

  const firstCohortProductTitle = currentProductMeta?.title;

  const summaryText = useMemo(() => {
    const lapseMetricColumns =
      data.lapseMetricColumns.data.length > 0
        ? data.lapseMetricColumns.data
        : [];

    const firstCohortSummary = {
      firstLapseMetricName: data.lapseMetricColumns.data[0],
      firstLapseMetricValue: firstCohort[
        data.lapseMetricColumns.data[0]
      ]?.toLocaleString(locale, { style: 'percent', minimumFractionDigits: 2 }),
      name: firstCohort
        ? isValid(parse(firstCohort.cohort_name, 'yyyy-MM', new Date()))
          ? format(
              parse(firstCohort.cohort_name, 'yyyy-MM', new Date()),
              'MMMM yyyy'
            )
          : firstCohortProductTitle
        : '...',
      customers: firstCohort
        ? firstCohort?.num_buyers?.toLocaleString(locale)
        : '...',
      ano: firstCohort ? firstCohort?.ano?.toLocaleString(locale) : '...',
      repeats: firstCohort
        ? firstCohort?.num_repeat?.toLocaleString(locale)
        : '...',
      repeat_perc: firstCohort
        ? firstCohort?.repeat_perc?.toLocaleString(locale, { style: 'percent' })
        : '...',
      ror_30:
        firstCohort && lapseMetricColumns.length
          ? (firstCohort[lapseMetricColumns[0]] || 0)?.toLocaleString(
              String(locale),
              {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 2,
              }
            )
          : '...',
      ror_60:
        firstCohort && lapseMetricColumns.length > 1
          ? (firstCohort[lapseMetricColumns[1]] || 0)?.toLocaleString(
              String(locale),
              {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 2,
              }
            )
          : '...',
    };

    return base_string.replace(
      /{{(.*?)}}/g,
      (match, key: keyof typeof firstCohortSummary) => {
        return `<span className="font-bold">${firstCohortSummary[key]}</span>`;
      }
    );
  }, [
    base_string,
    data.lapseMetricColumns.data,
    firstCohort,
    firstCohortProductTitle,
    locale,
  ]);

  return (
    <p className="mt-2">
      <span className="font-bold">The first row can be read as: </span>
      {parseHtml(summaryText)}
    </p>
  );
}
function ChurnRate({
  data,
  currency,
  locale,
  base_string,
}: {
  data: Props;
  currency: string;
  locale: string;
  base_string: string;
}) {
  const firstCohort = useMemo(() => {
    return data.tableData.length > 0 ? data.tableData[0] : {};
  }, [data.tableData]);

  const currentProductMeta = useAppSelector((state) => {
    return state.productMeta.find(
      (productMeta) => productMeta.id === firstCohort.cohort_name
    );
  });
  const firstCohortProductTitle = currentProductMeta?.title;

  const summaryText = useMemo(() => {
    const lapseMetricColumns =
      data.lapseMetricColumns.data.length > 0
        ? data.lapseMetricColumns.data
        : [];
    const firstCohortSummary = {
      firstLapseMetricName: data.lapseMetricColumns.data[0],
      firstLapseMetricValue: firstCohort[
        data.lapseMetricColumns.data[0]
      ]?.toLocaleString(locale, { style: 'percent', minimumFractionDigits: 2 }),
      name: firstCohort
        ? isValid(parse(firstCohort.cohort_name, 'yyyy-MM', new Date()))
          ? format(
              parse(firstCohort.cohort_name, 'yyyy-MM', new Date()),
              'MMMM yyyy'
            )
          : firstCohortProductTitle
        : '...',
      customers: firstCohort
        ? firstCohort?.num_buyers?.toLocaleString(locale)
        : '...',
      churned_customers: firstCohort
        ? firstCohort?.num_churned?.toLocaleString(locale)
        : '...',
      churned_percent: firstCohort
        ? firstCohort?.churned_perc?.toLocaleString(locale, {
            style: 'percent',
          })
        : '...',
      atd: firstCohort ? firstCohort?.atd?.toLocaleString(locale) : '...',
      churnr_30:
        firstCohort && lapseMetricColumns.length
          ? (firstCohort[lapseMetricColumns[0]] || 0)?.toLocaleString(
              String(locale),
              {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 2,
              }
            )
          : '...',
      churnr_90:
        firstCohort && lapseMetricColumns.length > 2
          ? (firstCohort[lapseMetricColumns[2]] || 0)?.toLocaleString(
              String(locale),
              {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 2,
              }
            )
          : '...',
    };

    return base_string.replace(
      /{{(.*?)}}/g,
      (match, key: keyof typeof firstCohortSummary) => {
        return `<span className="font-bold">${firstCohortSummary[key]}</span>`;
      }
    );
  }, [
    base_string,
    data.lapseMetricColumns.data,
    firstCohort,
    firstCohortProductTitle,
    locale,
  ]);

  return (
    <p className="mt-2">
      <span className="font-bold">The first row can be read as: </span>
      {parseHtml(summaryText)}
    </p>
  );
}

function RevenueGrowthSummary({
  data,
  currency,
  locale,
  base_string,
}: {
  data: Props;
  currency: string;
  locale: string;
  base_string: string;
}) {
  const firstCohort = useMemo(() => {
    return data.tableData.length > 0 ? data.tableData[0] : {};
  }, [data.tableData]);

  const currentProductMeta = useAppSelector((state) => {
    return state.productMeta.find(
      (productMeta) => productMeta.id === firstCohort.cohort_name
    );
  });

  const firstCohortProductTitle = currentProductMeta?.title;

  const summaryText = useMemo(() => {
    const lapseMetricFirstColumn =
      data.lapseMetricColumns.data.length > 0 &&
      data.lapseMetricColumns.data[0];
    const firstCohortSummary = {
      firstLapseMetricName: data.lapseMetricColumns.data[0],
      firstLapseMetricValue: firstCohort[
        data.lapseMetricColumns.data[0]
      ]?.toLocaleString(locale, {
        style: 'currency',
        minimumFractionDigits: 2,
        currency: currency ?? 'usd',
      }),
      name: firstCohort
        ? isValid(parse(firstCohort.cohort_name, 'yyyy-MM', new Date()))
          ? format(
              parse(firstCohort.cohort_name, 'yyyy-MM', new Date()),
              'MMMM yyyy'
            )
          : firstCohortProductTitle
        : '...',
      customers: firstCohort
        ? firstCohort?.num_buyers?.toLocaleString(locale)
        : '...',
      orders: firstCohort
        ? firstCohort?.num_orders?.toLocaleString(locale)
        : '...',
      aov: firstCohort
        ? Math.round(firstCohort?.aov || 0)?.toLocaleString(String(locale), {
            style: 'currency',
            currency: currency ?? 'usd',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
        : '...',
      revenue: firstCohort
        ? Math.round(firstCohort?.gross_revenue || 0)?.toLocaleString(
            String(locale),
            {
              style: 'currency',
              currency: currency ?? 'usd',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }
          )
        : '...',
      arpu_30:
        firstCohort && lapseMetricFirstColumn
          ? Math.round(
              firstCohort[lapseMetricFirstColumn] || 0
            )?.toLocaleString(String(locale), {
              style: 'currency',
              currency: currency ?? 'usd',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
          : '...',
    };

    return base_string.replace(
      /{{(.*?)}}/g,
      (match, key: keyof typeof firstCohortSummary) => {
        return `<span className="font-bold">${firstCohortSummary[key]}</span>`;
      }
    );
  }, [
    base_string,
    currency,
    data.lapseMetricColumns.data,
    firstCohort,
    firstCohortProductTitle,
    locale,
  ]);

  return (
    <p className="mt-2">
      <span className="font-bold">The first row can be read as: </span>
      {parseHtml(summaryText)}
    </p>
  );
}
